import React from 'react';
import { STATUS, isFilled } from '../state/model';
import StatusBadge from './StatusBadge';

// The core UX primitive, redesigned per the Claude Design handoff: each datum
// is a quiet tile — label row with a small אשר action (auto-extracted) or a
// ✓ אומת mark, the value as a large editable input, then note/warning and the
// evidence-source link pinned to the tile's bottom. Conflicts render as an
// explicit decision: pick one of the conflicting source values.
//
// Editing a value flips status to MANUAL; approving flips it to APPROVED;
// picking a conflict option resolves it to APPROVED with the chosen value.

// A note is a *warning* (amber fill) when the field is conflicted or the text
// carries a caveat; otherwise it's informational context (plain gray).
function isWarningNote(f) {
  if (f.status === STATUS.CONFLICT) return true;
  return /סתירה|יש לאמת|יש להכריע|יש לבדוק|טעון|אזהרה|עשוי|ברירת מחדל/.test(f.note || '');
}

export default function Field({
  label,
  field,
  onChange,
  type = 'text',
  options = [],
  placeholder = '',
  readOnly = false,
  hint = null,
  big = false,
  plain = false,
  anchor = null,
}) {
  const f = field || { value: null, status: STATUS.MISSING };

  const setValue = (value) => {
    const empty = value === null || value === undefined || value === '';
    onChange({ value: empty ? null : value, status: empty ? STATUS.MISSING : STATUS.MANUAL });
  };

  // Plain variant: optional manual fields (project metadata, free-text notes).
  // No verify flow, no source chrome, no "missing" warning styling — they are
  // not extraction tasks, just things a user may fill in.
  if (plain) {
    return (
      <div className="rd-field-plain" data-rd-anchor={anchor || undefined}>
        <label className="rd-field-plabel">{label}</label>
        {type === 'select' ? (
          <select className="rd-input-plain" value={f.value ?? ''} disabled={readOnly} onChange={(e) => setValue(e.target.value)}>
            <option value="">— בחר —</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        ) : type === 'textarea' ? (
          <textarea className="rd-input-plain rd-textarea" value={f.value ?? ''} readOnly={readOnly} placeholder={placeholder} onChange={(e) => setValue(e.target.value)} />
        ) : (
          <input className="rd-input-plain" value={f.value ?? ''} readOnly={readOnly} placeholder={placeholder} onChange={(e) => setValue(e.target.value)} />
        )}
      </div>
    );
  }
  const approve = () => onChange({ status: STATUS.APPROVED });
  const pickConflictOption = (opt) => onChange({
    value: opt.value,
    status: STATUS.APPROVED,
    note: `הוכרע ידנית — נבחר ${opt.label}`,
  });

  const isConflict = f.status === STATUS.CONFLICT;
  const isMissing = !isFilled(f);
  const showApprove = f.status === STATUS.AUTO && isFilled(f);
  const showVerified = f.status === STATUS.APPROVED;
  const showEstimated = f.status === STATUS.ESTIMATED;

  // Free-text areas (notes) shouldn't scream "missing" — the dashed amber
  // treatment is for data the calculation actually needs.
  const tileClass = [
    'rd-field',
    showVerified ? 'is-verified' : '',
    isConflict ? 'is-conflict' : '',
    isMissing && !isConflict && type !== 'textarea' ? 'is-missing' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={tileClass} data-rd-anchor={anchor || undefined}>
      <div className="rd-field-head">
        <label className="rd-field-label">{label}</label>
        <div className="rd-field-meta">
          {showEstimated && <StatusBadge status={f.status} title={f.source ? `מקור: ${f.source}` : null} />}
          {showApprove && (
            <button type="button" className="rd-approve-btn" onClick={approve} title="אשר נתון">
              אשר
            </button>
          )}
          {showVerified && <span className="rd-verified-mark">✓ אומת</span>}
        </div>
      </div>

      {isConflict && f.conflictOptions?.length ? (
        <>
          <div className="rd-conflict-text">סתירה בין מקורות — בחרו ערך:</div>
          <div className="rd-conflict-opts">
            {f.conflictOptions.map((opt, i) => (
              <button key={i} type="button" className="rd-conflict-opt" onClick={() => pickConflictOption(opt)}>
                <span>{opt.label}</span>
                <span className="pick">בחר</span>
              </button>
            ))}
          </div>
        </>
      ) : type === 'select' ? (
        <select
          className="rd-input"
          value={f.value ?? ''}
          disabled={readOnly}
          onChange={(e) => setValue(e.target.value)}
        >
          <option value="">— בחר —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          className="rd-input rd-textarea"
          value={f.value ?? ''}
          readOnly={readOnly}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
        />
      ) : (
        <input
          className="rd-input"
          style={big ? { fontSize: 26, fontWeight: 700 } : undefined}
          type={type === 'number' ? 'number' : 'text'}
          value={f.value ?? ''}
          readOnly={readOnly}
          placeholder={placeholder || (isMissing ? 'השלימו ידנית' : '')}
          onChange={(e) => setValue(type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
        />
      )}

      {isConflict && f.note && <div className="rd-field-note">{f.note}</div>}
      {!isConflict && f.note && (
        isWarningNote(f)
          ? <div className="rd-field-note">⚠ {f.note}</div>
          : <div className="rd-field-note rd-field-note-info">{f.note}</div>
      )}
      {hint && <div className="rd-field-hint">{hint}</div>}
      {f.source && (
        f.sourceUrl
          ? <a className="rd-field-source rd-field-source-link" href={f.sourceUrl} target="_blank" rel="noreferrer">מקור: {f.source} ↗</a>
          : <div className="rd-field-source">מקור: {f.source}</div>
      )}
    </div>
  );
}
